import type { ReactNode } from 'react';
import type {
  FmOperationalRegionDescriptor,
  FmOperationalWorkspaceDescriptor,
} from './FmOperationalWorkspaceRoot.types';
import { storyChartHasData } from './elements/chartElementSpec';
import type { FmElement } from './elements/elements.types';
import { hasMapSlot } from '@/shared/config/mapSlotRegistry';

type SectionRegion = Extract<FmOperationalRegionDescriptor, { kind: 'section' }>;

/**
 * Headline figures say what the selection is worth, so they stay readable
 * (each figure naming its own absence) before anything is selected.
 */
function servesWithoutSelection(region: FmOperationalRegionDescriptor): boolean {
  return region.kind === 'section' && region.section.arrangement === 'figures';
}

/** Whether a ledger, chart or map element has anything served to show. */
function elementServes(element: FmElement): boolean {
  if (element.element === 'table') {
    if (element.table) return element.table.rows.length > 0;
    if (element.rankedTable) return element.rankedTable.rows.length > 0;
    return true;
  }
  if (element.element === 'chart') return element.chart ? storyChartHasData(element.chart) : true;
  if (element.element === 'map') {
    const { figure } = element;
    if (!figure) return false;
    return figure.kind === 'asset' || hasMapSlot(figure.slotKey);
  }
  return true;
}

/**
 * A region whose ledger, chart or map serves nothing reads as the region's own
 * unavailable surface, which names the missing thing, stamps why on the region
 * and carries the control that produces it. Every other state is the section's
 * own, through the shared section state notice.
 */
function sectionServes(region: SectionRegion): boolean {
  const { section } = region;
  if (!('slots' in section) || !section.slots) return true;
  const content = (Object.values(section.slots) as readonly (readonly FmElement[])[])
    .flat()
    .filter(
      (element) =>
        element.element === 'table' || element.element === 'chart' || element.element === 'map',
    );
  return content.length === 0 || content.some(elementServes);
}

export function regionServesContent(
  descriptor: FmOperationalWorkspaceDescriptor,
  region: FmOperationalRegionDescriptor,
): boolean {
  if (
    region.scope === 'selection' &&
    !servesWithoutSelection(region) &&
    descriptor.selection.kind === 'none'
  ) {
    return false;
  }
  if (region.kind === 'handoff') return region.destinations.length > 0;
  return sectionServes(region);
}

export type RegionBody = {
  replaced: boolean;
  loading: boolean;
  unavailable: boolean;
};

export function regionBody({
  descriptor,
  region,
  stateSurface,
  stateSurfaceMode,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  region: FmOperationalRegionDescriptor;
  stateSurface?: ReactNode;
  stateSurfaceMode?: 'replace' | 'lead';
}): RegionBody {
  const replaced = Boolean(stateSurface) && stateSurfaceMode === 'replace';
  const loading = descriptor.state === 'loading';
  const unavailable = !loading && !replaced && !regionServesContent(descriptor, region);
  return { replaced, loading, unavailable };
}

export function regionUsesStoryRail(
  region: FmOperationalRegionDescriptor,
  replaced: boolean,
  unavailable: boolean,
): boolean {
  return !regionOwnsItsHeading(region) && Boolean(region.lede) && !replaced && !unavailable;
}

function leadElement(region: SectionRegion) {
  const { section } = region;
  if (!('slots' in section) || !section.slots) return undefined;
  return Object.values(section.slots).flat()[0];
}

/**
 * Served evidence names its own heading, a carousel names each slide, and a
 * split whose copy carries a heading prints it beside its media, so the region
 * leaves its heading to them.
 */
export function regionOwnsItsHeading(region: FmOperationalRegionDescriptor): boolean {
  if (region.kind !== 'section') return false;
  if (region.section.arrangement === 'carousel') return true;
  const lead = leadElement(region);
  if (lead?.element === 'provenance') return true;
  return (
    region.section.arrangement === 'split' && lead?.element === 'copy' && Boolean(lead.heading)
  );
}
